// AuthProvider — laadt huidige user uit /api/auth/me.
// Geen wachtwoord-login meer; identity komt via Toolbox SSO.
// Als /me 401 geeft of geen user terugkomt, stuurt RequireAuth (zie App.tsx) de
// browser naar /api/auth/login die op zijn beurt redirect naar Toolbox.
import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import type { UserPublic } from '@recotex/shared';
import { api } from './api';
import { Sentry } from './sentry';

type AuthState = {
  user: UserPublic | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => void;
};

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserPublic | null>(null);
  const [loading, setLoading] = useState(true);

  // Enkel de eerste keer toont de app een laadscherm (loading start op true). Later verversen gebeurt
  // stil, zodat de open pagina niet herlaadt (bv. na het kiezen van de persoon achter een gedeeld account).
  const refresh = useCallback(async () => {
    try {
      const meResp = await api.auth.me();
      setUser(meResp.user);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Sentry: koppel fouten aan de ingelogde gebruiker (enkel id/e-mail/rol).
  useEffect(() => {
    Sentry.setUser(user ? { id: user.id || user.email, email: user.email, role: user.role } : null);
  }, [user]);

  const signOut = () => setUser(null);

  return <Ctx.Provider value={{ user, loading, refresh, signOut }}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

/** Stuur de browser naar de server-side login-redirect. */
export function redirectToLogin(): void {
  window.location.assign('/api/auth/login');
}
